import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuthStore } from 'stores/authStore';
import { login } from 'utils/authUtils';
import { Loader2, AlertCircle } from 'lucide-react';
import Button from 'components/Button';
import { ROUTES } from 'utils/constants';
import styles from './AuthCallback.module.scss';
import { devlogs } from 'utils/devlogs';

function AuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isLoading = useAuthStore((state) => state.isLoading);
  const authHandleAuthCallback = useAuthStore(
    (state) => state.handleAuthCallback,
  );
  const [error, setError] = useState<string | null>(null);
  /** Guards duplicate async work in one mount; not in effect deps (that caused a retry loop on error). */
  const processingRef = useRef(false);

  // Primitives only: `searchParams` from react-router changes identity often (e.g. other hooks
  // call setSearchParams), which re-fired this effect and retried exchange → loops / CSRF noise.
  const code = searchParams.get('code');
  const returnedState = searchParams.get('state');
  const errorParam = searchParams.get('error');
  const errorDescriptionParam = searchParams.get('error_description');

  useEffect(() => {
    if (processingRef.current) {
      devlogs('[auth/callback]', 'effect skipped: already processing');
      return;
    }

    devlogs('[auth/callback]', 'effect', {
      pathname: window.location.pathname,
      hasCode: Boolean(code),
      codeLength: code?.length,
      hasState: Boolean(returnedState),
      errorParam: errorParam ?? null,
    });

    if (errorParam) {
      const errorDescription = errorDescriptionParam || 'Authorization failed';
      devlogs('[auth/callback]', 'OAuth error from redirect', {
        errorParam,
        errorDescription,
      });
      setError(errorDescription);
      return;
    }

    if (!code) {
      devlogs('[auth/callback]', 'abort: no code in query');
      setError('No authorization code received');
      return;
    }

    processingRef.current = true;
    void (async () => {
      try {
        devlogs(
          '[auth/callback]',
          'calling handleAuthCallback (exchange + session)',
        );
        const path = await authHandleAuthCallback(
          code,
          returnedState || undefined,
        );
        devlogs('[auth/callback]', 'exchange flow finished, navigating', {
          path,
        });
        navigate(path, { replace: true });
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'Failed to complete authentication';
        devlogs('[auth/callback]', 'handleAuthCallback failed', err);
        setError(errorMessage);
        processingRef.current = false;
      }
    })();
  }, [
    code,
    returnedState,
    errorParam,
    errorDescriptionParam,
    authHandleAuthCallback,
    navigate,
  ]);

  const handleRetry = () => {
    void login();
  };

  const handleGoHome = () => {
    navigate(ROUTES.HOME, { replace: true });
  };

  // Don't show anything while auth is loading (including token refresh)
  if (isLoading) {
    return null;
  }

  return (
    <div className={styles.loadingContainer}>
      {error ? (
        <>
          <AlertCircle className={styles.errorIcon} size={48} />
          <h2 className={styles.title}>Authentication Failed</h2>
          <p className={styles.message}>{error}</p>
          <div className={styles.actions}>
            <Button onClick={handleRetry} className={styles.retryButton}>
              Try Again
            </Button>
            <Button
              onClick={handleGoHome}
              variant="outline"
              className={styles.homeButton}
            >
              Go to Home
            </Button>
          </div>
        </>
      ) : (
        <>
          <Loader2 className={styles.loadingIcon} size={48} />
          <p className={styles.loadingText}>Redirecting...</p>
        </>
      )}
    </div>
  );
}

export default AuthCallback;
